import { z } from 'zod';

// Full DB rows CAD reads back through the data provider's peekRecord
const cadComponentSchema = z.looseObject({ id: z.string() });

const cadOrthoImageSchema = z.object({
  active: z.boolean().nullish(),
  height: z.number().nullish(),
  id: z.string(),
  name: z.string(),
  opacity: z.number().nullish(),
  source: z.string(),
  url: z.string().nullish(),
  width: z.number().nullish(),
  worldfile_a: z.number().nullish(),
  worldfile_b: z.number().nullish(),
  worldfile_c: z.number().nullish(),
  worldfile_d: z.number().nullish(),
  worldfile_e: z.number().nullish(),
  worldfile_f: z.number().nullish(),
});

// Aurora's CAD share payload (GET /api/cad_iframe_shares/<token>), down to what the viewer reads. Only the design is
// required: anything else missing just means less on screen, not a failed view
export const cadShareSchema = z.object({
  design: z.object({
    components_hash: z
      .object({
        inverters: z.array(cadComponentSchema).default([]),
        optimizers: z.array(cadComponentSchema).default([]),
        panels: z.array(cadComponentSchema).default([]),
      })
      .prefault({}),
    // Opaque to us; CAD loads it as is
    design_model_json: z.string(),
  }),
  project: z.object({
    latitude: z.number().nullish(),
    longitude: z.number().nullish(),
    ortho_images: z.array(cadOrthoImageSchema).default([]),
    project_type: z.string().nullish(),
    time_zone_offset: z.number().nullish(),
  }),
});

export type CadShare = z.infer<typeof cadShareSchema>;
export type CadShareOrthoImage = z.infer<typeof cadOrthoImageSchema>;

export type CadPanel = { annual_production_kwh: number; id: string };

// The panels to show for one panel count, e.g. `{panel_count: 3, panel_ranks: [0, 1, 4]}` shows panels[0], panels[1]
// and panels[4]
export type CadPanelSelection = { panel_count: number; panel_ranks: number[] };

// consumer-platform's CadView (GET /api/v1/visitor/<vid>/quotes/<qs_id>/cad/), all null without a finished design.
// `share_url` carries the Aurora share token, so it's never logged. `panels` is every design panel, highest
// production first; `panel_selections` has one entry per panel count
export type CadView = {
  panel_selections?: CadPanelSelection[] | null;
  panels?: CadPanel[] | null;
  share_url?: string | null;
};

// What the browser gets: the share, every design panel id ranked by production (null: show the design as saved), and
// which of those panels to show for each panel count (null: the top N)
export type CadDesign = {
  panelSelections: CadPanelSelection[] | null;
  rankedPanelIds: string[] | null;
  share: CadShare;
};

// A panel in CAD's `solarPanelLayout` event, down to what the viewer reads. `id` changes each time CAD enables or
// disables the panel; its roof face and x,y (metres on the face, null when tilted or rotated) don't
export type CadLayoutPanel = {
  id: string;
  isEnabled: boolean;
  roofFaceId: string;
  roofFaceX: number | null;
  roofFaceY: number | null;
};
